import { useEffect } from 'react';

import {
  Button,
  ButtonGroup,
  Editor,
  ErrorsType,
  FormFeedback,
  FormGroup,
  FormLabel,
  FormRow,
  FormSection,
  ImageUpload,
  Input,
  SwitchCheck,
} from '@opositandopn/ui';
import { useFormikContext } from 'formik';
import { IoSyncCircleOutline } from 'react-icons/io5';

import { ConfirmStepButton } from '../../../../../components/shared/admin/layout/ConfirmStepButton';
import { DeleteStepButton } from '../../../../../components/shared/admin/layout/DeleteStepButton';
import { StepperBody } from '../../../../../components/shared/admin/layout/StepperBody';
import { StepperFooter } from '../../../../../components/shared/admin/layout/StepperFooter';
import { editorLabels } from '../../../../../components/shared/editor/EditorLabels';
import { BaseComponentProps } from '../../../../../types/base-component.types';
import { SubjectsForm } from '../../../../subjects/components/subjects-form/SubjectsForm';
import { TagsForm } from '../../../../tags/components/tags-form/TagsForm';
import { MultipleChoiceAssessmentFormValues } from '../MultipleChoiceAssessmentEditor';

import styles from './MultipleChoiceAssessmentStep.module.scss';

type ContainerProps = BaseComponentProps & {
  onNext: () => void;
  onCancel: () => void;
  isDeleting: boolean;
  onDelete: () => void;
  isResettingMetrics: boolean;
  /**
   * Whether the assessment is still unreleased. The API refuses to reset the
   * metrics of a released one, so the button is disabled rather than left to
   * fail — read from the stored release date, not the edited one, which the
   * form may hold unsaved.
   */
  canResetMetrics: boolean;
  onResetMetrics: () => void;
};

function MultipleChoiceAssessmentStep({
  onNext,
  onCancel,
  isDeleting,
  onDelete,
  isResettingMetrics,
  canResetMetrics,
  onResetMetrics,
}: ContainerProps) {
  const {
    setTouched,
    values,
    touched,
    errors,
    isSubmitting,
    handleChange,
    setFieldTouched,
    setErrors,
  } = useFormikContext<MultipleChoiceAssessmentFormValues>();

  useEffect(() => {
    if (values.id) {
      setTouched(
        Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      );
    }
  }, [values]);

  return (
    <>
      <StepperBody>
        <FormSection title={values.id ? 'Editar simulacro' : 'Nuevo simulacro'}>
          <SubjectsForm />
          <FormGroup>
            <FormLabel>Nombre</FormLabel>
            <Input
              id="name"
              value={values.name}
              onChange={handleChange}
              error={touched.name && !!errors.name}
            />
            {touched.name && errors.name && (
              <FormFeedback variant="danger">{errors.name}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <FormLabel>Descripción</FormLabel>
            <Editor
              placeholder="Escribe aquí la descripción del simulacro..."
              labels={editorLabels}
              value={values.description}
              onChange={(value) => {
                handleChange({
                  target: {
                    name: 'description',
                    value,
                  },
                });
              }}
              error={touched.description && !!errors.description}
            />
            {touched.description && errors.description && (
              <FormFeedback variant="danger">{errors.description}</FormFeedback>
            )}
            <FormFeedback>
              Describe el simulacro de manera clara y concisa, para que los
              usuarios puedan entenderlo y prepararse para la prueba.
            </FormFeedback>
          </FormGroup>
          <TagsForm />
          <FormGroup>
            <FormLabel>Imagen asociada</FormLabel>
            <ImageUpload
              dataURL={values.base64Image}
              onChange={(image?: string) => {
                handleChange({
                  target: {
                    name: 'base64Image',
                    value: image ?? '',
                  },
                });
              }}
              onError={(error?: ErrorsType) => {
                setFieldTouched('base64Image', true, false);

                if (error?.maxFileSize) {
                  setErrors({
                    ...errors,
                    base64Image: 'La imagen no puede superar los 5MB',
                  });
                }

                if (error?.maxNumber) {
                  setErrors({
                    ...errors,
                    base64Image: 'No puedes subir más de 1 imagen',
                  });
                }

                if (error?.acceptType) {
                  setErrors({
                    ...errors,
                    base64Image: 'El formato de la imagen no es válido',
                  });
                }

                if (error?.resolution) {
                  setErrors({
                    ...errors,
                    base64Image: 'La resolución de la imagen es muy alta',
                  });
                }
              }}
            />
            {touched.base64Image && errors.base64Image && (
              <FormFeedback variant="danger">{errors.base64Image}</FormFeedback>
            )}
            <FormFeedback>
              La imagen no puede superar los 5MB, y debe estar en un formato
              válido (jpg, jpeg, png).
            </FormFeedback>
          </FormGroup>
        </FormSection>

        <FormSection title="Preguntas">
          <FormRow>
            <FormGroup>
              <FormLabel>Número de opciones</FormLabel>
              <ButtonGroup
                label="Número de opciones"
                onSelect={(value: string | number) =>
                  handleChange({
                    target: {
                      name: 'numberOfChoices',
                      value: +value,
                    },
                  })
                }
                disabled={values.id !== undefined}
                error={touched.numberOfChoices && !!errors.numberOfChoices}
                options={[
                  {
                    value: 3,
                    label: '3',
                    selected: values.numberOfChoices === 3,
                  },
                  {
                    value: 4,
                    label: '4',
                    selected: values.numberOfChoices === 4,
                  },
                  {
                    value: 5,
                    label: '5',
                    selected: values.numberOfChoices === 5,
                  },
                ]}
              />
              {touched.numberOfChoices && errors.numberOfChoices && (
                <FormFeedback variant="danger">
                  {errors.numberOfChoices}
                </FormFeedback>
              )}
              <FormFeedback>
                Selecciona el número de opciones que van a contener las
                preguntas.
              </FormFeedback>
            </FormGroup>

            <FormGroup>
              <FormLabel>Disposición</FormLabel>
              <SwitchCheck
                label="Mantener el orden de las preguntas"
                checked={values.maintainsQuestionOrder}
                onChange={() => {
                  handleChange({
                    target: {
                      name: 'maintainsQuestionOrder',
                      value: !values.maintainsQuestionOrder,
                    },
                  });
                }}
              />
              {touched.maintainsQuestionOrder &&
                errors.maintainsQuestionOrder && (
                  <FormFeedback variant="danger">
                    {errors.maintainsQuestionOrder}
                  </FormFeedback>
                )}
            </FormGroup>
          </FormRow>
        </FormSection>

        <FormSection title="Metadatos">
          <FormRow>
            <FormGroup>
              <FormLabel>Tipo de simulacro</FormLabel>
              <ButtonGroup
                label="Tipo de simulacro"
                onSelect={(value: string | number) => {
                  handleChange({
                    target: {
                      name: 'purpose',
                      value: value === 'formative' ? 'formative' : 'summative',
                    },
                  });
                  handleChange({
                    target: {
                      name: 'referenceStandard',
                      value:
                        value === 'formative'
                          ? 'criterion-referenced'
                          : 'norm-referenced',
                    },
                  });
                  handleChange({
                    target: {
                      name: 'methodology',
                      value: 'quantitative',
                    },
                  });
                  handleChange({
                    target: {
                      name: 'perspective',
                      value: 'instructor',
                    },
                  });
                  handleChange({
                    target: {
                      name: 'setting',
                      value: value === 'formative' ? 'formal' : 'standardized',
                    },
                  });
                  handleChange({
                    target: {
                      name: 'official',
                      value: value === 'official',
                    },
                  });
                }}
                error={touched.setting && !!errors.setting}
                options={[
                  {
                    value: 'formative',
                    label: 'Formativo',
                    selected: values.setting === 'formal',
                  },
                  {
                    value: 'competitive',
                    label: 'Simulacro competitivo',
                    selected:
                      values.setting === 'standardized' && !values.official,
                  },
                  {
                    value: 'official',
                    label: 'Examen oficial',
                    selected:
                      values.setting === 'standardized' && values.official,
                  },
                ]}
              />
              {touched.setting && errors.setting && (
                <FormFeedback variant="danger">{errors.setting}</FormFeedback>
              )}
              <FormFeedback>Selecciona el tipo de simulacro.</FormFeedback>
            </FormGroup>
            <FormRow>
              <FormGroup>
                <FormLabel>Tiempo límite</FormLabel>
                <Input
                  id="timeLimit"
                  type="number"
                  value={values.timeLimit / 60}
                  min={1}
                  max={50}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange({
                      target: {
                        name: 'timeLimit',
                        value: +e.target.value * 60,
                      },
                    });
                  }}
                  error={touched.timeLimit && !!errors.timeLimit}
                />
                {touched.timeLimit && errors.timeLimit && (
                  <FormFeedback variant="danger">
                    {errors.timeLimit}
                  </FormFeedback>
                )}
                <FormFeedback>
                  El tiempo límite en minutos para completar el simulacro.
                </FormFeedback>
              </FormGroup>

              <FormGroup>
                <FormLabel>Nota de corte</FormLabel>
                <Input
                  id="cutoffScore"
                  type="number"
                  value={values.cutoffScore}
                  min={0}
                  max={10}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange({
                      target: {
                        name: 'cutoffScore',
                        value: e.target.value === '' ? 0 : +e.target.value,
                      },
                    });
                  }}
                  error={touched.cutoffScore && !!errors.cutoffScore}
                />
                {touched.cutoffScore && errors.cutoffScore && (
                  <FormFeedback variant="danger">
                    {errors.cutoffScore}
                  </FormFeedback>
                )}
                <FormFeedback>
                  Si introduces un valor, se mostrará como nota de corte en
                  lugar de la calculada automáticamente por el sistema.
                </FormFeedback>
              </FormGroup>
            </FormRow>
          </FormRow>
        </FormSection>
      </StepperBody>

      <StepperFooter className={styles['footer']}>
        <div className={styles['footer__left']}>
          {values.id && (
            <>
              <DeleteStepButton
                title="¿Estás segurx de que deseas eliminar este simulacro?"
                disabled={isSubmitting}
                isDeleting={isDeleting}
                onDelete={onDelete}
              />
              <ConfirmStepButton
                icon={<IoSyncCircleOutline />}
                tone="secondary"
                tooltip={
                  canResetMetrics
                    ? 'Reiniciar estadísticas'
                    : 'Este simulacro está visible. Sus estadísticas no se pueden borrar.'
                }
                title="¿Estás segurx de que deseas reiniciar las estadísticas de este simulacro?"
                subtitle="Se borrarán las notas y los tiempos de todos los usuarios. Esta acción no se puede deshacer."
                confirmLabel="Reiniciar"
                disabled={isSubmitting || isDeleting || !canResetMetrics}
                isPending={isResettingMetrics}
                onConfirm={onResetMetrics}
                testId="reset-metrics-step-button"
              />
            </>
          )}
        </div>
        <div className={styles['footer__right']}>
          <Button
            type="button"
            variant="outline-danger"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            disabled={isSubmitting}
            onClick={() => {
              setTouched(
                Object.keys(values).reduce(
                  (acc, key) => ({ ...acc, [key]: true }),
                  {},
                ),
              );

              if (Object.keys(errors).length === 0) {
                onNext();
              }
            }}
          >
            Siguiente
          </Button>
        </div>
      </StepperFooter>
    </>
  );
}

export { MultipleChoiceAssessmentStep };
